/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { Card, Grid, CircularProgress, CardContent, Typography } from "@mui/material";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import BubbleChart from "examples/Charts/BubbleChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import MDTypography from "components/MDTypography";

import { useEffect, useState } from "react";
import { getTopDashboardLayer, getPieChartData } from "services/api";
import { useAuth } from "context/authContext";
import { getBranches } from "services/api";
import { calculateAverageLocation } from "services/utils";
import EgyptMap from "components/Map";
import { useTranslation } from "react-i18next";
import MyGantt from "components/Gantt";
import { getInitiativesByCompanyId } from "services/api";
import { getTopDashboardUser } from "services/api";
import { buildStyles, CircularProgressbarWithChildren } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { fetchRiskMatrices } from "services/utils";

function Dashboard() {
  const { t } = useTranslation();
  const [topCardsData, setTopCardsData] = useState([]);
  const [piedata, setPieData] = useState([]);
  // const [riskMatrix, setRiskMatrix] = useState([]);
  // const [matrixLabels, setMatrixLabels] = useState([]);
  const [scorecardsCompletion, setScorecardsCompletion] = useState();

  const [initiatives, setInitiatives] = useState([]);
  const [riskMatrices, setRiskMatrices] = useState([]);
  const [riskMatrixChart, setRiskMatrixChart] = useState([]);

  const { employee } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      /*
        TopDashboard Data Structure: List [Employee Count, Department Count, Job Count, Branch Count]
      */
      const today = new Date();
      const companyId = employee.companyId;

      //TOP CARDS
      const [responseTopDashboard, percentage, initiativesData] = await Promise.all([
        getTopDashboardLayer(),
        getTopDashboardUser(today.getMonth() + 1, today.getFullYear()),
        getInitiativesByCompanyId(companyId),
      ]);

      if (responseTopDashboard != "error") {
        setTopCardsData(responseTopDashboard);
      }

      //GetPie chart data
      if (piedata?.length < 3) {
        const responsePie = await getPieChartData();
        if (responsePie != "error") {
          responsePie.forEach((element) => {
            setPieData((prevPieData) => [...prevPieData, transformData(element)]);
          });
        }
      }

      //ScorecardCompletion
      if (percentage != "error") {
        setScorecardsCompletion([percentage[2], percentage[3]]);
      }

      await fetchRiskMatrices(setRiskMatrices, setRiskMatrixChart);

      if (initiativesData != "error") {
        setInitiatives(initiativesData);
      }
    };

    fetchData();
  }, []);

  function transformData(data) {
    const labels = [];
    const values = [];

    data.forEach((item) => {
      const [label, count] = item.split(","); // Split name and count
      labels.push(label.trim());
      values.push(parseInt(count.trim())); // Convert count to number
    });

    return {
      labels: labels,
      datasets: {
        label: t("admin.dashboard.noEmp"),
        data: values,
      },
    };
  }

  return (
    <>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="business"
                title={t("admin.dashboard.branches")}
                count={topCardsData?.length > 3 ? topCardsData[3] : 0}
                isAdmin={true}
                data={1}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="groupwork"
                title={t("admin.dashboard.departments")}
                count={topCardsData?.length > 1 ? topCardsData[1] : 0}
                isAdmin={true}
                data={2}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="work"
                title={t("admin.dashboard.jobs")}
                count={topCardsData?.length > 2 ? topCardsData[2] : 0}
                isAdmin={true}
                data={2}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="person"
                title={t("admin.dashboard.employees")}
                count={topCardsData?.length > 0 ? topCardsData[0] : 0}
                isAdmin={true}
                data={3}
              />
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mt={2}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={6} lg={4} sx={{ display: "flex" }}>
              <Card
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                  border: "1px solid #f1f5f9",
                }}
              >
                {piedata.length > 0 ? (
                  <DefaultDoughnutChart chart={piedata} />
                ) : (
                  <MDBox flex={1} display="flex" flexDirection="column" alignItems="center" p={2}>
                    <MDTypography variant="h6" align="center">
                      {t("admin.dashboard.donutPerDep")}
                    </MDTypography>

                    <CircularProgress size={20} sx={{ mt: 4 }} />
                  </MDBox>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4} sx={{ display: "flex" }}>
              <Card
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <MDTypography variant="h6" align="center" mb={2} mt={2}>
                  {t("admin.dashboard.scorecardPercentage")}
                </MDTypography>

                <MDBox flex={1} display="flex" justifyContent="center" alignItems="center">
                  {scorecardsCompletion && scorecardsCompletion[1] > 0 ? (
                    <div style={{ width: 200, height: 200 }}>
                      <CircularProgressbarWithChildren
                        value={(scorecardsCompletion[0] / scorecardsCompletion[1]) * 100}
                        strokeWidth={12}
                        styles={buildStyles({
                          pathColor: "#00B100",
                          trailColor: "#E0E0E0",
                          strokeLinecap: "round",
                        })}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "16px",
                            color: "#2E2E2E",
                          }}
                        >
                          <strong>
                            {Math.round((scorecardsCompletion[0] / scorecardsCompletion[1]) * 100)}%
                          </strong>
                          <div style={{ fontSize: "14px" }}>
                            {scorecardsCompletion[0]}/{scorecardsCompletion[1]}
                          </div>
                        </div>
                      </CircularProgressbarWithChildren>
                    </div>
                  ) : (
                    <CircularProgress size={20} />
                  )}
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4} sx={{ display: "flex" }}>
              <Card
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <MDBox py={2} px={2} flex={1} display="flex" flexDirection="column">
                  {/* Title */}
                  <MDBox mb={2}>
                    <MDTypography variant="h6" align="center">
                      {t("admin.dashboard.riskMatrix")}
                    </MDTypography>
                  </MDBox>

                  {/* Chart */}
                  <MDBox flex={1} display="flex" justifyContent="center" alignItems="center">
                    {riskMatrixChart?.length > 0 ? (
                      <BubbleChart
                        color="primary"
                        title={t("admin.dashboard.riskMatrix")}
                        height={200}
                        processedData={riskMatrixChart}
                        labels={{
                          x: t("admin.strategicInfo.riskManagement.impact"),
                          y: t("admin.strategicInfo.riskManagement.likelihood"),
                        }}
                      />
                    ) : (
                      <CircularProgress size={20} />
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox>
          <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
            {/* Gantt Chart Section */}
            <Grid item sx={{ width: "100%", mt: 2 }}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    overflow: "auto", // enables scroll when needed
                    maxHeight: 400, // or set this dynamically based on screen
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "none",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t("user.dashboards.corporateInitiatives")}
                  </Typography>
                  {initiatives ? (
                    <MyGantt initiatives={initiatives} />
                  ) : (
                    <MDBox height="100%" display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress size={20} />
                    </MDBox>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </>
  );
}

export default Dashboard;
